import styled from 'styled-components'
import { X } from 'src/icons'
import Modal from 'src/components/Modal'
import PersonCard from 'src/components/PersonCard'
import { OrgNode } from './index'

// Bolder/tighter than UserModal's CloseIcon (which is small and light-gray,
// reading as disconnected from the card here) — matches the mockup's clearly
// visible close button.
const CloseIcon = styled(X)`
	width: 22px;
	height: 22px;
	color: ${(props) => props.theme.colors.lightestGray};
	top: 22px;
	right: 22px;
	position: absolute;
	z-index: 1;
	&:hover {
		cursor: pointer;
		color: ${(props) => props.theme.colors.primaryTextColor};
	}
`

// Modal's own DialogContainer fills the viewport (position:fixed, 100%/100%);
// centering the card here with flex instead of magic top/left percentages
// keeps it centered and on-screen at any viewport size.
const CenteringLayer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-height: 100%;
	padding: 24px;
`

// Same visual treatment as UserModal's PersonCardContainer, but sized with
// clamped relative units instead of a bare 450px (which overflowed narrow
// viewports) and height:auto instead of a fixed 250px (which cut off longer
// org-chart titles/contact rows).
const PersonCardContainer = styled.div`
	position: relative;
	background-color: ${({ theme }) => theme.colors.white};
	width: min(450px, 100%);
	box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
	border-radius: 3px;
	border: 1px solid ${({ theme }) => theme.colors.borderGray};
	padding: 12px;
	color: ${({ theme }) => theme.colors.primaryTextColor};

	/* PersonCard's own card is the visible keyline — full width so it sits an even
	   12px inside the modal edge, instead of its default 90% (off-centre here). */
	> div {
		box-sizing: border-box;
		width: 100%;
		margin-bottom: 0;
	}

	/* Larger photo than the shared 64px, per the design. */
	img {
		width: 120px;
		height: 120px;
	}

	/* Align the name's visible glyph top with the top of the photo. Flush boxes
	   leave the text low (half-leading plus the font's ascent), so it needs a
	   negative offset — measured by pixel-scanning the rendered text, since
	   font metrics measure to the ascender and overshoot by ~2px. */
	h1 {
		margin-top: -3px;
		text-align: left;
		line-height: 1.1;
	}
`

const PersonDetailModal = ({ person, onClose }: { person: OrgNode; onClose: () => void }) => {
	return (
		<Modal backgroundColor="dark">
			<CenteringLayer>
				<PersonCardContainer>
					<CloseIcon onClick={onClose} />
					<PersonCard
						person={{
							profilePhotoUrl: person.profilePhotoUrl as string | undefined,
							firstName: person.firstName as string,
							lastName: person.lastName as string,
							jobTitle: person.jobTitle as string,
							department: person.department as { title: string },
							jobsite: person.jobsite as { title: string },
							phoneExt: person.phoneExt as string,
							phoneCell: person.phoneCell as string,
							phoneDirect: person.phoneDirect as string,
							email: person.email as string
						}}
						fullWidth
					/>
				</PersonCardContainer>
			</CenteringLayer>
		</Modal>
	)
}

export default PersonDetailModal
